import * as React from 'react';
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { Rate, Row, Col } from 'antd';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as uuid from "uuid";

import { DataLocalService } from 'src/core/services/data-local.service';
import { TextView } from 'src/core/ui/text.view';
import { TimePickerView, DisableKind } from 'src/core/ui/timepicker.view';
import { MapDialogView } from 'src/core/ui/map-dialog.view';
import { CardController } from 'src/app/shared/card.controller';
import { ProductCategoryEditorController } from 'src/app/products/product-categories/parts/product-category-editor.controller';
import { TabLanguageController } from 'src/core/ui/language/tab-language.controller';
import { RegionEditorController } from 'src/app/general/regions/parts/region-editor.controller';
import { AirportEditorController } from 'src/app/general/airports/parts/airport-editor.controller';
import { AirlineEditorController } from 'src/app/general/airlines/parts/airline-editor.controller';
import { PlaceEditorController } from 'src/app/general/places/parts/place-editor.controller';
import { ContractsAgeBandController } from 'src/app/products/contract/form/parts/contract-ageband.controller';
import { ContractCustomOccupanciesView } from 'src/app/products/contract/form/parts/contract-custom-occupancies.view';
import { actionCreators } from "src/store/products/product-form-nav-actions";
import { type } from 'src/core/services/data/JayDataContext';

interface PropsFromDispatch {
	categoryLoaded: (product: ProductItem, category: ProductCategorySummary) => any;
}

interface Props extends ComponentViewProps<ProductItem> {
	mode: ControlMode;
	productCodeRequired: boolean;
	contextService: any;
}

@observer
class ProductGeneralView extends React.Component<Props & PropsFromDispatch, {}> {
	@observable
	private occupancyDataSet: DataSet<any>;

	public componentDidMount() {
		this.occupancyDataSet = this.dataSetCreator(this.props.model.dynamicProperties["customOccupancyConfigs"]);
	}

	public componentWillReceiveProps(nextProps: Readonly<Props & PropsFromDispatch>) {
		this.occupancyDataSet = this.dataSetCreator(nextProps.model.dynamicProperties.customOccupancyConfigs);
	}

	public render() {
		const { categoryLoaded, mode, model, productCodeRequired, setBusy } = this.props;
		const translations = (model as any).dynamicProperties['translations'];
		const displayNames = (model as any).dynamicProperties['displayName'];
		const array: TabLanguageModel[] = [
			{
				title: 'Title',
				dynamicProperties: displayNames,
				required: true
			},
			{
				title: 'Description',
				dynamicProperties: translations,
				rows: 5
			}
		];
		return (
			<Row gutter={16}>
				<Col xs={24} lg={14}>
					<CardController icon='file' title="General info" mode={mode} className='card-borderless'>
						<TextView
							onGetErrorMessage={() => model.validate(false, 'code')}
							mode={mode}
							label="Product Code"
							required={productCodeRequired}
							value={model.code}
							onChanged={newVal => model.code = newVal}
						/>
						<ProductCategoryEditorController
							required
							mode={mode}
							onGetErrorMessage={() => model.validate(false, 'categoryId')}
							onChanged={(value) => {
								value && categoryLoaded(model, value);
								model.categoryId = value != null ? value.id : null;
							}}
							value={model.categoryId}
							productType={model.productType}
							setBusy={setBusy}
							afterLoad={res => {
								res && categoryLoaded(model, res);
							}}
						/>
						<RegionEditorController
							mode={mode}
							onGetErrorMessage={() => model.validate(false, 'regionId')}
							onChanged={(value) => {
								model.regionId = value != null ? value.id : null;
							}}
							value={model.regionId}
							required
							setBusy={setBusy}
						/>
						<PlaceEditorController
							mode={model.regionId == null ? 'display' : mode}
							regionId={model.regionId}
							onChanged={(value) => model.placeId = value != null ? value.id : null}
							value={model.placeId}
							setBusy={setBusy}
						/>
						<MapDialogView
							location={model.location}
							onChangeLocation={e => model.location = e}
							mode={mode}
							zoom={13}
						/>
						<TabLanguageController
							dynamicProperties={array}
							mode={mode}
							setBusy={setBusy}
							isRichTextBox={false}
							model={model}
						/>
					</CardController>
				</Col>
				<Col xs={24} lg={10}>
					{
						model.productType == 1 &&
						this.renderAccommodation()
					}
					{
						model.productType == 3 &&
						this.renderTransfer()
					}
					{
						model.productType == 4 &&
						this.renderFlight()
					}
					{this.renderPaxConfig()}
				</Col>
			</Row>
		)
	}

	private renderAccommodation(): React.ReactNode {
		const model = this.props.model;
		const rating = model.dynamicProperties['accomodation'] != null ? model.dynamicProperties['accomodation']['rating'] : 0;

		return (
			<CardController icon='icon-hotel' title="Accommodation" mode={this.props.mode} className='card-borderless'>
				<label>Rating: </label>
				<br />
				<Rate
					allowHalf={false}
					value={rating}
					onChange={(value) => {
						if (model.dynamicProperties['accomodation'] == null)
							model.dynamicProperties['accomodation'] = { 'rating': value }
						else
							model.dynamicProperties['accomodation']['rating'] = value;
						this.forceUpdate();
					}}
					disabled={this.props.mode != 'edit'}
				/>
				<TimePickerView
					label="Check In"
					required
					mode={this.props.mode}
					value={model.dynamicProperties['accomodation'] != null ? model.dynamicProperties['accomodation']['checkIn'] : null}
					onChanged={(value: Date) => {
						const date = value ? value.toISOString() : undefined;
						if (model.dynamicProperties['accomodation'] == null)
							model.dynamicProperties['accomodation'] = { 'checkIn': date }
						else
							model.dynamicProperties['accomodation']['checkIn'] = date;
						this.forceUpdate();
					}}
					disableKind={DisableKind.disableSeconds}
					dateUsage="utc"
				/>
				<TimePickerView
					label="Check Out"
					required
					mode={this.props.mode}
					value={model.dynamicProperties['accomodation'] != null ? model.dynamicProperties['accomodation']['checkOut'] : null}
					onChanged={(value) => {
						const date = value ? value.toISOString() : undefined;
						if (model.dynamicProperties['accomodation'] == null) {
							model.dynamicProperties['accomodation'] = { 'checkOut': date }
						} else {
							model.dynamicProperties['accomodation']['checkOut'] = date;
						}
						this.forceUpdate();
					}}
					disableKind={DisableKind.disableSeconds}
					dateUsage="utc"
				/>
			</CardController>
		)
	}

	private renderTransfer(): React.ReactNode {
		const model = this.props.model;

		return (
			<CardController icon='icon-train' title="Transfer" mode={this.props.mode} className='card-borderless'>
				<PlaceEditorController
					label="Origin Place"
					onGetErrorMessage={() => model.validate(false, 'originPlaceId')}
					mode={model.regionId == null ? 'display' : this.props.mode}
					onChanged={(value) => {
						const id = value != null ? value.id : null;
						if (model.dynamicProperties['transfer'] == null) {
							model.dynamicProperties['transfer'] = { 'originPlaceId': id };
						} else {
							model.dynamicProperties['transfer']['originPlaceId'] = id;
						}
					}}
					value={model.dynamicProperties['transfer'] != null ? model.dynamicProperties['transfer']['originPlaceId'] : null}
					required={true}
					setBusy={this.props.setBusy}
					regionId={model.regionId}
				/>
				<PlaceEditorController
					label="Destination Place"
					onGetErrorMessage={() => model.validate(false, 'destinationPlaceId')}
					mode={model.regionId == null ? 'display' : this.props.mode}
					onChanged={(value) => {
						const id = value != null ? value.id : null;
						if (model.dynamicProperties['transfer'] == null) {
							model.dynamicProperties['transfer'] = { 'destinationPlaceId': id };
						} else {
							model.dynamicProperties['transfer']['destinationPlaceId'] = id;
						}
					}}
					value={model.dynamicProperties['transfer'] != null ? model.dynamicProperties['transfer']['destinationPlaceId'] : null}
					required={true}
					setBusy={this.props.setBusy}
					regionId={model.regionId}
				/>
				<TextView
					onGetErrorMessage={() => model.validate(false, 'code')}
					mode={this.props.mode}
					label="Time Duration (in minutes)"
					placeholder="Time Duration"
					required={true}
					value={model.dynamicProperties['transfer'] != null ? model.dynamicProperties['transfer']['duration'] : null}
					onChanged={(value) => {
						if (model.dynamicProperties['transfer'] == null) {
							model.dynamicProperties['transfer'] = { 'duration': value };
						} else {
							model.dynamicProperties['transfer']['duration'] = value;
						}
					}}
				/>
			</CardController>
		)
	}

	private renderFlight(): React.ReactNode {
		const model = this.props.model;

		return (
			<CardController icon='icon-plane' title="Transfer" mode={this.props.mode} className='card-borderless'>
				<AirportEditorController
					onGetErrorMessage={() => this.props.model.validate(false, 'originAirportId')}
					label="Origin Airport"
					mode={this.props.mode}
					onChanged={(value) => {
						if (model.dynamicProperties['flight'] == null) {
							model.dynamicProperties['flight'] = { 'originAirportId': value };
						} else {
							model.dynamicProperties['flight']['originAirportId'] = value;
						}
					}}
					value={model.dynamicProperties['flight'] != null ? model.dynamicProperties['flight']['originAirportId'] : null}
					required={true}
					setBusy={this.props.setBusy}
				/>
				<AirportEditorController
					label="Destination Airport"
					onGetErrorMessage={() => this.props.model.validate(false, 'destinationAirportId')}
					mode={this.props.mode}
					onChanged={(value) => {
						if (model.dynamicProperties['flight'] == null) {
							model.dynamicProperties['flight'] = { 'destinationAirportId': value };
						} else {
							model.dynamicProperties['flight']['destinationAirportId'] = value;
						}
					}}
					value={model.dynamicProperties['flight'] != null ? model.dynamicProperties['flight']['destinationAirportId'] : null}
					required={true}
					setBusy={this.props.setBusy}
				/>
				<AirlineEditorController
					onGetErrorMessage={() => this.props.model.validate(false, 'airlineId')}
					label="Airline"
					mode={this.props.mode}
					onChanged={(value) => {
						if (model.dynamicProperties['flight'] == null) {
							model.dynamicProperties['flight'] = { 'airlineId': value };
						} else {
							model.dynamicProperties['flight']['airlineId'] = value;
						}
					}}
					value={model.dynamicProperties['flight'] != null ? model.dynamicProperties['flight']['airlineId'] : null}
					required={true}
					setBusy={this.props.setBusy}
				/>
			</CardController>
		)
	}

	private renderPaxConfig(): React.ReactNode {
		let ageband: AgeBandItem[] = this.props.model.dynamicProperties['ageBands'];
		if (ageband == null) {
			this.props.model.dynamicProperties['ageBands'] = [];
			ageband = this.props.model.dynamicProperties['ageBands'];
		}
		const maxPax = this.props.model.dynamicProperties["maxPax"] || 0;

		return (
			<React.Fragment>
				<ContractsAgeBandController
					mode={this.props.mode}
					model={ageband}
					setBusy={this.props.setBusy}
					updateAgeBands={this.updateAgeBands}
				/>
				<CardController icon='setting' title="Paxs config" mode={this.props.mode} className='card-borderless'>
					<TextView mode={this.props.mode}
						label='Max Pax'
						value={maxPax}
						onChanged={newVal => {
							this.props.model.dynamicProperties["maxPax"] = newVal;
							this.forceUpdate();
						}}
						required={true}
					/>
					{
						ageband && ageband.length > 0 && this.occupancyDataSet  && <ContractCustomOccupanciesView
							ageBands={ageband.map(e => e.label?e.label:e.type.toString())}
							getAllOccupancies={this.getAllOccupancies}
							mode={this.props.mode}
							model={maxPax}
							newOccupancy={this.newOccupancy}
							occupancyDataSet={this.occupancyDataSet}
							setBusy={this.props.setBusy}
						/>
					}
				</CardController>
			</React.Fragment>
		);
	}

	private dataSetCreator(items: CustomOccupancyPax[]): DataSet<CustomOccupancyPax> {
		if (items == null) {
			this.props.model.dynamicProperties.customOccupancyConfigs = [];
		}

		//this.props.model.customOccupancyConfigs = items.slice(); // to force to be an "observable array" instead a proxy
		return new DataLocalService(this.props.model.dynamicProperties.customOccupancyConfigs);
	}

	private updateAgeBands = (items: AgeBandItem[], oldType?: string, newType?: string) => {
		let elements: CustomOccupancyPax[] = this.props.model.dynamicProperties["customOccupancyConfigs"];
		if (!elements) {
			this.props.model.dynamicProperties["customOccupancyConfigs"] = [];
			elements = this.props.model.dynamicProperties["customOccupancyConfigs"];
		}
		for (const item of items) {
			for (const ele of elements) {
				if (oldType != null && newType != null) {
					const aux = ele[oldType];
					ele[oldType] = undefined;
					ele[newType] = aux;
				} else {
					ele[item.label?item.label.toLocaleLowerCase():item.type.toString().toLowerCase()] = undefined;
				}
			}
		}
	}

	private getAllOccupancies = (maxPax: number) => {
		this.occupancyDataSet = this.dataSetCreator([]);
		let possibilities = [];
		for (let i = 0; i <= maxPax; i++)
			possibilities.push(i);
		let array = [];
		const ageBands = this.props.model.dynamicProperties['ageBands'].map(e => e.label ? e.label:e.type.toString());
		for (let i = 0; i < ageBands.length; i++) {
			array.push(possibilities);
		}
		let result = [];
		if (array.length == 1) {
			for (let i = 0; i < array[0].length; i++) {
				let newArr = [array[0][i]];
				result.push(newArr);
			}
		} else {
			result = this.allPossibleCases(array, maxPax);
		}
		result = result.filter(x => x.reduce((a, b) => a + b, 0) > 0);
		for (let res of result) {
			const occupancy = this.createOccupancyByPossibility(res);
			this.occupancyDataSet.create(() => occupancy)
		}
	}

	private createOccupancyByPossibility(possibilities: number[]) {
		const occupancy: CustomOccupancyPax = {
			id: uuid.v4()
		};
		const ageBands = this.props.model.dynamicProperties['ageBands'].map(e => e.label ? e.label : e.type.toString());
		for (let name = 0; name < ageBands.length; name++) {
			occupancy[ageBands[name].toLocaleLowerCase()] = possibilities[name].toString();
		}
		return occupancy;
	}

	private allPossibleCases(arr: any[], target: number) {
		if (arr.length === 0) {
			return [];
		} else if (arr.length === 1) {
			return arr[0];
		} else {
			const result = [];
			const allCasesOfRest = this.allPossibleCases(arr.slice(1), target);  // recur with the rest of array
			for (let c of allCasesOfRest) {
				for (let i = 0; i < arr[0].length; i++) {
					let newArr = [arr[0][i]];
					newArr = newArr.concat(c);
					let sum = newArr.reduce((a, b) => a + b, 0);
					if (sum <= target)
						result.push(newArr);
				}
			}
			return result;
		}
	}

	protected newOccupancy = () => {
		const occupancy = this.createOccupancy();
		return this.occupancyDataSet.create(() => occupancy);
	}

	private createOccupancy() {
		const occupancy: CustomOccupancyPax = {
			id: uuid.v4()
		};
		const ageBands = this.props.model.dynamicProperties['ageBands'].map(e => e.label ? e.label : e.type.toString());
		for (let name of ageBands) {
			occupancy[name] = '0';
		}
		return occupancy;
	}
}

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
	categoryLoaded: (
		product: ProductItem,
		category: ProductCategorySummary
	) => dispatch(actionCreators.productFormRelationshipLoaded(product, category, "category")),
});

const connected = connect(null, mapDispatchToProps)(ProductGeneralView);
export { connected as ProductGeneralView };



// WEBPACK FOOTER //
// ./src/app/products/products/form/parts/product-general.view.tsx